import React, { useRef, useState, useEffect } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { removeSelectedOrgAction, sendBroadcastMsg } from './broadcastSlice/actionCreators';
import { getSelectedOrgs } from './broadcastSlice';
import Pencil from '../../assets/icons/pen-with-side-line--yellow.svg';
import { translate } from '../../utils/translate';
import ChatBox from '../../components/Chat/ChatBox';
import { saveDocumentToServer } from '../../apis/mediaAPI';
import type { CreateMessageModel } from '../Chat/models';
import Loader from '../../components/Loader/Loader';
import {
  createMessageIsLoading,
  selectSubject,
  setIsLoading,
  setSubject,
} from '../CreateMessage/createMessageSlice';
import {
  SPage,
  BroadcastView,
  SubjectContainer,
  SubjectLabel,
  SubjectInput,
  SubjectIcon,
} from './BroadcastNew.styles';
import { RecipientsSummaryList } from '../../components/RecipientsSummaryList/RecipientsSummaryList';
import { RecipientType } from '../RecipientGroups/RecipientGroups';
import type { AppDispatch } from '../../store';

const BroadcastNew = (props: {
  setShowBottomTabs: React.Dispatch<React.SetStateAction<boolean>>;
  removeSelectedOrgAction: (id: number) => void;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedOrgs = useAppSelector(getSelectedOrgs);
  const isLoading = useAppSelector(createMessageIsLoading);
  const [tabBar, setTabBar] = useState(true);
  const subjectText = useAppSelector(selectSubject);

  const subjectRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(setIsLoading(false));
    if (selectedOrgs.length === 0) {
      navigate('/broadcast');
    }
  }, [selectedOrgs]);

  const onRemoveOrg = (id: number) => {
    props.removeSelectedOrgAction(id);
  };

  const sendMessage = async (
    text: string | undefined,
    imageFileNames?: string[],
    documentFileNames?: string[],
    audioFileNames = [],
    locationId?: number
  ) => {
    const message: CreateMessageModel = {
      subject: subjectText ?? null,
      documentFileNames: documentFileNames,
      audioFileNames: audioFileNames,
      photoFileNames: imageFileNames,
      text: text,
      locationId: locationId,
      subOrganizationID: _.head(selectedOrgs)?.id,
      organizationIds: selectedOrgs.map(e => e.id),
      Type: 0,
    };
    dispatch(sendBroadcastMsg(message, navigate));
  };

  const onPhotosSend = async (imagesList: string[], messageText: string) => {
    if (imagesList.length > 0) {
      const text = messageText && messageText.length > 0 ? messageText : undefined;
      sendMessage(text, imagesList);
    }
  };

  const onDocumentsSend = async (documentsList: File[], messageText: string) => {
    const documentsFilesNamesArray = [];
    let document: File;
    for await (document of documentsList) {
      const formData = new FormData();
      formData.append('document', document);
      const result = await saveDocumentToServer(formData);
      if (result) {
        documentsFilesNamesArray.push(result);
      }
    }
    if (documentsFilesNamesArray.length > 0) {
      sendMessage(messageText, [], documentsFilesNamesArray);
    }
  };

  const highlightText = () => {
    if (subjectRef.current) subjectRef.current.focus();
  };

  if (isLoading) return <Loader />;

  return (
    <SPage>
      <BroadcastView role="broadcastCreateContainer">
        <RecipientsSummaryList
          data={selectedOrgs}
          handleAddClick={() => navigate('/broadcast')}
          handleRemoveClick={onRemoveOrg}
          recipientGroupsRoute={'/broadcast/recipient-accounts'}
          recipientsType={RecipientType.Accounts}
        />

        <SubjectContainer role="subjectContainer">
          <SubjectLabel role="subjectLabel">{translate('messages_subject_add')}</SubjectLabel>

          <SubjectInput
            role="subjectInput"
            type="text"
            name="subject"
            value={subjectText}
            placeholder={translate('messages_subject_add_placeholder')}
            onChange={e => dispatch(setSubject(e.target.value))}
            ref={subjectRef}
          />

          <SubjectIcon onClick={highlightText} role="subjectIcon">
            <img role="icon" src={Pencil} alt="addGroup" />
          </SubjectIcon>
        </SubjectContainer>
      </BroadcastView>

      <ChatBox
        hideLocationOption
        tabBar={tabBar}
        setTabBar={setTabBar}
        onTextSend={sendMessage}
        onPhotosSend={onPhotosSend}
        onDocumentsSend={onDocumentsSend}
        onLocationSend={(locationId, text) => sendMessage(text, [], [], [], locationId)}
      />
    </SPage>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    removeSelectedOrgAction: (id: number) => dispatch(removeSelectedOrgAction(id)),
  };
};

export default connect(null, mapDispatchToProps)(BroadcastNew);
